import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { combineLatest } from 'rxjs';

import { AppHeaderService } from 'src/app/services/app-header/app-header.service';
import { FirebaseService } from 'src/app/services/firebase/firebase.service';
import { HelpIntroService } from 'src/app/services/help-intro/help-intro.service';
import { OrganizationService } from 'src/app/services/organization/organization.service';
import { UserService } from 'src/app/services/user/user.service';

/**
 * When the concepts tour shipped. Accounts created before this instant never
 * get it automatically. A floor only: the first-session rules below keep every
 * account that existed on deploy day quiet even if the deploy lands days after
 * this date (review T1). Move it if the release date moves.
 */
export const TOUR_RELEASED_AT = Date.parse('2026-10-20T00:00:00Z');

/**
 * "New" means FIRST SESSION: the Auth account was created less than this long
 * ago. A sign-up opens the app at once (creation IS the first sign-in), so a
 * day covers a slow email verification or org setup; an account that has been
 * around for days is an existing user on deploy day, even with no tour state
 * on its doc (review T1: "created 5 days ago, never seen => no tour").
 */
export const TOUR_FIRST_SESSION_WINDOW_MS = 24 * 60 * 60 * 1000;

/**
 * First session, second check: Firebase Auth `metadata.lastSignInTime` is the
 * last time the user SIGNED IN (password, link, provider — not a token
 * refresh), and equals `creationTime` at sign-up. A later sign-in means a new
 * session on an account that already had one, so no tour. The slack covers a
 * sign-up flow that signs in again right away (email link, provider linking).
 */
export const TOUR_FIRST_SIGN_IN_SLACK_MS = 30 * 60 * 1000;

/** How the tour closed: "Get started" on the last slide (`done`), or Skip / Esc / ✕ (`skip`). */
export type TourExit = 'done' | 'skip';

const STORAGE_PREFIX = 'crwd.tour.seen.';
/** Dot-path merge into `users/{uid}`: only this field is written. */
export const TOUR_SEEN_FIELD = 'onboardingTour.seenAt';

export interface TourDecisionInput {
  /** `users/{uid}.onboardingTour.seenAt`, when the doc carries it. */
  remoteSeenAt?: number;
  /** The localStorage mirror (first paint, offline write). */
  localSeenAt?: number | null;
  /** Firebase Auth `metadata.creationTime`, epoch ms (NaN / undefined when unknown). */
  accountCreatedAt?: number;
  /** Firebase Auth `metadata.lastSignInTime`, epoch ms (NaN / undefined when unknown: age rule only). */
  lastSignInAt?: number;
  now: number;
}

/**
 * The "new user" rule, pure so it is unit-tested on its own. Open automatically
 * only when ALL hold:
 * - the tour was never seen (user doc OR this browser);
 * - the Auth account was created at or after {@link TOUR_RELEASED_AT};
 * - first session: created less than {@link TOUR_FIRST_SESSION_WINDOW_MS} ago,
 *   AND (when known) not signed in again since sign-up beyond
 *   {@link TOUR_FIRST_SIGN_IN_SLACK_MS}.
 * An unknown creation time means "not new" — the safe side is to stay quiet;
 * "Take the tour" is always there.
 */
export function shouldAutoOpenTour(input: TourDecisionInput): boolean {
  if (input.remoteSeenAt || input.localSeenAt) return false;
  const created = input.accountCreatedAt;
  if (created === undefined || !Number.isFinite(created)) return false;
  if (created < TOUR_RELEASED_AT) return false;
  if (input.now - created > TOUR_FIRST_SESSION_WINDOW_MS) return false;
  const signedIn = input.lastSignInAt;
  if (signedIn !== undefined && Number.isFinite(signedIn) && signedIn - created > TOUR_FIRST_SIGN_IN_SLACK_MS) return false;
  return true;
}

const toMs = (value: string | undefined): number | undefined => (value ? Date.parse(value) : undefined);

/**
 * The concepts tour (Chats, Topics, Agents, Teams carousel): opens by itself
 * once for a NEW user, reopens from the user menu ("Take the tour"). "Seen" is
 * stored on `users/{uid}.onboardingTour.seenAt` (owner-only update, no rules
 * change) and mirrored to localStorage. 0 extra reads: the user doc arrives
 * through UserService's existing listener.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingTourService {
  private readonly user = inject(UserService);
  private readonly firebase = inject(FirebaseService);
  private readonly appHeader = inject(AppHeaderService);
  private readonly organization = inject(OrganizationService);
  private readonly helpIntro = inject(HelpIntroService);
  private readonly destroyRef = inject(DestroyRef);

  /** Whether the tour modal is showing. */
  public readonly open = signal(false);
  /** The slide to show when it opens. */
  public readonly startIndex = signal(0);

  private started = false;
  /** uid the automatic check already ran for (once per sign-in). */
  private checkedUid: string | null = null;
  /** `onboardingTour.seenAt` from the latest user-doc snapshot. */
  private remoteSeenAt: number | undefined;

  /** Starts watching sign-ins; idempotent. */
  public start(): void {
    if (this.started) return;
    this.started = true;
    // The automatic check waits for every gate (review T2), so the tour never
    // opens over sign-up, email verification or org creation:
    // - the signed-in shell is showing (sidebar/header);
    // - the organization is loaded (orgId$ emits once its doc arrived);
    // - the user doc came from the SERVER: a cached snapshot may predate a
    //   seenAt written on another device. A cached one is skipped, not
    //   consumed: the check runs on the next server snapshot.
    const sub = combineLatest([this.user.onboardingTour$, this.appHeader.headerVisible$, this.organization.orgId$])
      .subscribe(([tour, shellVisible, orgId]) => {
        if (!tour) return;
        // A cached seenAt still proves "seen" (no write); a cached absence does not.
        if (tour.seenAt || !tour.fromCache) this.remoteSeenAt = tour.seenAt;
        if (!shellVisible || !orgId || !this.organization.data || tour.fromCache) return;
        this.maybeAutoOpen(tour);
      });
    this.destroyRef.onDestroy(() => sub.unsubscribe());
  }

  /** "Take the tour": always opens, on the first slide. */
  public reopen(): void {
    this.startIndex.set(0);
    this.open.set(true);
  }

  /**
   * Finished ("Get started"), skipped, Esc or ✕: never auto-open again. Then
   * the Help guide takes over (HelpIntroService): "Get started" may open Help
   * on its introduction, anything else points at the Help button.
   */
  public close(exit: TourExit = 'skip'): void {
    if (!this.open()) return;
    this.open.set(false);
    this.markSeen();
    this.helpIntro.afterTour(exit);
  }

  private maybeAutoOpen(tour: { seenAt?: number }): void {
    const uid = this.user.user?.uid;
    if (!uid || this.checkedUid === uid) return;
    this.checkedUid = uid;
    const open = shouldAutoOpenTour({
      remoteSeenAt: tour.seenAt,
      localSeenAt: this.readLocal(uid),
      accountCreatedAt: toMs(this.user.user?.metadata?.creationTime),
      lastSignInAt: toMs(this.user.user?.metadata?.lastSignInTime),
      now: Date.now(),
    });
    if (open) {
      this.startIndex.set(0);
      this.open.set(true);
    }
  }

  private markSeen(): void {
    const uid = this.user.user?.uid;
    if (!uid) return;
    const now = Date.now();
    try {
      if (!this.readLocal(uid)) localStorage.setItem(STORAGE_PREFIX + uid, String(now));
    } catch {
      // Private mode / quota: the user doc still records it.
    }
    // First-seen semantics (seenAt ??= now): a "Take the tour" reopen does not
    // overwrite when the user first saw it, and costs no write.
    if (this.remoteSeenAt) return;
    this.remoteSeenAt = now;
    this.firebase.globalFirestore.docUpdate(`users/${uid}`, { [TOUR_SEEN_FIELD]: now })
      .catch((err: unknown) => console.warn('[OnboardingTour] Could not save the tour as seen', err));
  }

  private readLocal(uid: string): number | null {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + uid);
      const value = raw ? Number(raw) : NaN;
      return Number.isFinite(value) ? value : null;
    } catch {
      return null;
    }
  }
}

/** Exposed for specs. */
export const TOUR_STORAGE_PREFIX = STORAGE_PREFIX;
